import { BaseApi, ApiResponse } from "../utils/request";

// 分页请求参数
export interface PageRequest {
  query: {
    pageNo: number;
    pageSize: number;
    status?: string;
    ddCreated?: {
      start?: number;
      finish?: number;
    };
    dtCreated?: {
      start?: number;
      finish?: number;
    };
    dtDate?: {
      start?: number;
      finish?: number;
    };
  };
}

// 分页数据（根据实际 API 返回格式定义）
// API 返回示例：
// {
//   "status": 0,
//   "time": 1760690853134,
//   "maxAge": 0,
//   "page": {
//     "number": 1,
//     "size": 10,
//     "numberOfElements": 0,
//     "totalPages": 0,
//     "content": [],
//     "totalElements": 0
//   }
// }
export interface PageData<T> {
  number: number; // 当前页码
  size: number; // 每页数量
  numberOfElements: number; // 当前页的元素数量
  totalPages: number; // 总页数
  content: T[]; // 数据列表
  totalElements: number; // 总元素数量
}

// 分页响应包装
export interface PageResponse<T> {
  maxAge: number;
  time: number;
  status: number;
  page: PageData<T>;
}

// 充值记录
export interface RechargeRecord {
  id: string;
  created: number;
  modified: number;
  userId: string;
  orderNo: string;
  amount: number;
  status: string; // PENDING, SUCCESS, FAILED, CANCELLED
  payType: string;
  payChannel: string;
  currencyId: string;
  currencySymbol: string;
  remark?: string;
}

// 奖金记录
export interface BonusRecord {
  id: string;
  created: number;
  modified: number;
  userId: string;
  type: string; // CHECK_IN, VIP_REWARD, REBATE, PROMOTION, etc.
  amount: number;
  status: string; // PENDING, RECEIVED, EXPIRED
  source: string;
  auditRatio: number;
  description?: string;
  expireTime?: number;
}

// 投注记录
export interface BetRecord {
  id: string;
  created: number;
  modified: number;
  userId: string;
  gameId: string;
  gameName: string;
  platformId: string;
  platformName: string;
  betAmount: number;
  winAmount: number;
  profit: number;
  status: string; // PENDING, WIN, LOSE, CANCELLED
  betTime: number;
  settleTime?: number;
  currencyId: string;
  currencySymbol: string;
  orderNo: string;
}

/**
 * Records API 类
 * 继承 BaseApi，提供记录查询相关的 API 接口
 */
export class RecordsApi extends BaseApi {
  constructor() {
    super();
  }

  /**
   * 获取充值记录分页
   * @param params 分页参数
   * @returns Promise<ApiResponse<PageResponse<RechargeRecord>, true>>
   */
  async getRechargePage(params: PageRequest): Promise<ApiResponse<any, true>> {
    return this.post<any, true>("/api/pay/recharge_page", params);
  }

  /**
   * 获取提现记录分页
   * @param params 分页参数
   * @returns Promise<ApiResponse<PageResponse<any>, true>>
   */
  async getWithdrawPage(
    params: PageRequest
  ): Promise<ApiResponse<PageResponse<any>, true>> {
    return this.post<PageResponse<any>, true>("/api/pay/withdraw_page", params);
  }

  /**
   * 获取奖金记录分页
   * @param params 分页参数
   * @returns Promise<ApiResponse<PageResponse<BonusRecord>, true>>
   */
  async getBonusPage(
    params: PageRequest
  ): Promise<ApiResponse<PageResponse<any>, true>> {
    return this.post<PageResponse<any>, true>("/api/user/bonus_page", params);
  }

  /**
   * 获取投注记录分页
   * @param params 分页参数
   * @returns Promise<ApiResponse<PageResponse<BetRecord>, true>>
   */
  async getBetRecordPage(
    params: PageRequest
  ): Promise<ApiResponse<PageResponse<any>, true>> {
    return this.post<PageResponse<any>, true>(
      "/api/game/bet_record_page",
      params
    );
  }

  /**
   * 获取奖金审核记录
   * @param params 查询参数
   * @returns Promise<ApiResponse<any, true>>
   */
  async getBounsAuditRecord(
    params: PageRequest
  ): Promise<ApiResponse<PageResponse<any>, true>> {
    return this.post<PageResponse<any>, true>("/api/user/audit_record", params);
  }

  async getGameList(): Promise<ApiResponse<any, true>> {
    return this.post<any, true>("/api/user/user_played_games", {});
  }
}

// 导出实例和便捷方法
export const recordsApi = new RecordsApi();

export const getRechargePage = (params: any) =>
  recordsApi.getRechargePage(params);

export const getWithdrawPage = (params: PageRequest) =>
  recordsApi.getWithdrawPage(params);

export const getBonusPage = (params: PageRequest) =>
  recordsApi.getBonusPage(params);

export const getBetRecordPage = (params: PageRequest) =>
  recordsApi.getBetRecordPage(params);

export const getBounsAuditRecord = (params: PageRequest) =>
  recordsApi.getBounsAuditRecord(params);

export const getGameList = () => recordsApi.getGameList();
